import { jwtDecode } from 'jwt-decode'

import { clearAuthToken, readAuthToken } from '@/helpers/authToken'
import { blueprintIdOf, isBlueprintId } from '@/helpers/franchiseMerchant'
import { platformsFromAttributes, resolveActivePlatform } from '@/services/platformRouting'
import { mapRelationship } from '@/store/mappers'

const decodeAuthToken = () => {
  try {
    return jwtDecode(readAuthToken())
  } catch {
    clearAuthToken()
    if (window.location.pathname !== '/login') {
      window.location = '/login?to=' + window.location.href
    }
  }
  return undefined
}

let getters = {
  audience: state => audienceId => {
    return state.audiences[audienceId]
  },

  loggedInUserId: state => {
    const decoded = decodeAuthToken()
    return decoded?.user_id ?? (decoded?.franchise_id ? state.activeFranchiseMerchantId || undefined : undefined)
  },

  franchiseTokenId: () => decodeAuthToken()?.franchise_id,

  isFranchise: state => !!state.franchise,

  franchiseMerchants: state => state.franchiseMerchants,

  franchiseBlueprintId: state => blueprintIdOf(state),

  isActingAsBlueprint: state => isBlueprintId(state.activeFranchiseMerchantId, blueprintIdOf(state)),

  activeFranchiseMerchant: (state, getters) =>
    getters.franchiseMerchants.find(merchant => merchant.id === state.activeFranchiseMerchantId),

  country: (state, getters) => {
    if (getters.loggedInUserId === undefined || getters.loggedInUserId === null) return 'USA'
    const user = mapRelationship({ type: 'user', id: getters.loggedInUserId.toString() })
    return user?.attributes.country ?? 'USA' // TODO(alicja): remove once https://bottlelabs.slack.com/archives/C01GFDWLM53/p1711117045456869 is fixed
  },

  message: state => messageId => {
    return state.messages[messageId]
  },

  attachment: state => attachmentId => {
    return state.attachments[attachmentId]
  },

  user: state => userId => {
    return state.users[userId]
  },

  call: state => callId => {
    return state.calls[callId]
  },

  activity: state => activityId => {
    return state.activities[activityId]
  },

  conversationUserPair: state => conversationUserPairId => {
    return state.conversationUserPairs[conversationUserPairId]
  },

  platforms: state => platformsFromAttributes(state.loggedInUser?.attributes),

  activePlatform: (state, getters) =>
    getters.isFranchise ? 'franchise' : resolveActivePlatform(state.activePlatformPreference, getters.platforms),

  isCatering: (state, getters) => getters.activePlatform === 'catering',

  hasMultiplePlatforms: (state, getters) => getters.platforms.length > 1,

  bottleBotEnabled: state => !!state.loggedInUser?.attributes?.bottle_bot_enabled,

  // Only rendered for a session the server has already verified as Bottle staff,
  // and omitted from every other render, so an absent key is the common case and
  // has to read as an ordinary merchant.
  bottleBotStaffPreview: state => !!state.loggedInUser?.attributes?.bottle_bot_staff_preview,

  isCateringContext: (state, getters) =>
    getters.platforms.length === 1 ? getters.platforms.includes('catering') : getters.isCatering,

  showBottleBot: (state, getters) => getters.bottleBotEnabled && getters.isCateringContext,

  ecommerceBotEnabled: state => !!state.loggedInUser?.attributes?.ecommerce_bot_enabled,

  ecommerceBotStaffPreview: state => !!state.loggedInUser?.attributes?.ecommerce_bot_staff_preview,

  isEcommerceContext: (state, getters) =>
    getters.platforms.length === 1 ? getters.platforms.includes('ecommerce') : getters.activePlatform === 'ecommerce',

  showEcommerceBot: (state, getters) => getters.ecommerceBotEnabled && getters.isEcommerceContext
}

export default getters
